import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import useFundsTransferStore from "../../../stores/fundsTransferStore";
import {
  useGetDisbursement,
  merchantQueryKeys,
} from "../../../hooks/queries/useMerchantQueries";
import { TransactionQueryKeys } from "../../../hooks/queries/useTransactionQueries";
import {
  POLL_TIMEOUT_MS,
  TERMINAL_DISBURSEMENT_STATUSES,
} from "../config/fundsTransferConfig";

export function usePollDisbursement() {
  const queryClient = useQueryClient();
  const { transactionId } = useFundsTransferStore(
    useShallow((state) => ({ transactionId: state.transactionId })),
  );

  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef(null);

  const { data: pollData } = useGetDisbursement(
    isPolling ? transactionId : null,
  );

  const status = pollData?.status;
  const pollDataRef = useRef(pollData);
  pollDataRef.current = pollData;

  const invalidateAfterDisbursement = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: merchantQueryKeys.merchantBalance(),
    });
    queryClient.invalidateQueries({
      queryKey: merchantQueryKeys.merchantDisbursementLimitsAll(),
    });
    queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
  }, [queryClient]);

  useEffect(() => {
    if (!status) return;
    if (!TERMINAL_DISBURSEMENT_STATUSES.has(status)) return;
    clearTimeout(timeoutRef.current);
    setIsPolling(false);
    useFundsTransferStore.getState().setDisbursementResult(pollDataRef.current);
    useFundsTransferStore.getState().setCurrentStep(4);
    invalidateAfterDisbursement();
  }, [status, invalidateAfterDisbursement]);

  useEffect(() => {
    if (!isPolling) return;
    timeoutRef.current = setTimeout(() => {
      setIsPolling(false);
      useFundsTransferStore.getState().setCurrentStep(4);
      invalidateAfterDisbursement();
    }, POLL_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [isPolling, invalidateAfterDisbursement]);

  const startPolling = useCallback(() => {
    if (!useFundsTransferStore.getState().transactionId) return;
    setIsPolling(true);
  }, []);

  return { isPolling, startPolling };
}
